// src/Phaser/engine/EventManager.ts

/**
 * Représente un événement collision
 */
export interface CollisionEventData {
  entityA: string;
  entityB: string;
}

export interface UserFunction {
  type: "UserFunction";
  params: string[]; // ex.: ["eventData"]
  body: any[];
  isArrow?: boolean;
}

export type CollisionCallbackAst = UserFunction;

type EventCallback = (...args: any[]) => void;

/** Fonctions publiques typées */
export type CollisionCallbackFn = (eventData: CollisionEventData) => void;
type CharacterObjectCollisionCallbackFn = (eventData: { charId: string; objId: string }) => void;
type ReachPositionCallbackFn = (eventData: { charId: string; x: number; y: number }) => void;
type KeyPressCallbackFn = (eventData: { key: string }) => void;
type ClickObjectCallbackFn = (eventData: { objId: string }) => void;
type TimerCallbackFn = (eventData: { seconds: number }) => void;
type PickupCallbackFn = (eventData: { charId: string; objId: string }) => void;

/** Cross‑env ID de timer (number en navigateur, NodeJS.Timeout en Node) */
type IntervalId = ReturnType<typeof setInterval>;
type TimeoutId = ReturnType<typeof setTimeout>;

/**
 * EventManager "Scratch-like"
 */
export class EventManager {
  // -------- générique on/emit/off ----------
  private genericListeners = new Map<string, EventCallback[]>();

  // -------- collisions perso-perso ----------
  private characterCollisionListeners = new Map<string, CollisionCallbackFn[]>();

  // -------- collisions perso-objet ----------
  private characterObjectCollisionListeners = new Map<string, CharacterObjectCollisionCallbackFn[]>();

  // -------- reach position ----------
  private reachPositionListeners = new Map<string, ReachPositionCallbackFn[]>();

  // -------- key press ----------
  private keyPressListeners = new Map<string, KeyPressCallbackFn[]>();

  // -------- click object ----------
  private clickObjectListeners = new Map<string, ClickObjectCallbackFn[]>();

  // -------- timers ----------
  private intervalIds: IntervalId[] = [];
  private timeoutIds: TimeoutId[] = [];

  // -------- pickup ----------
  private pickupListeners = new Map<string, PickupCallbackFn[]>();

  // -------- text events ----------
  private textStartsWithListeners = new Map<
    string,
    Array<{ substring: string; callbackFn: (ev: { textId: string; currentText: string }) => void }>
  >();

  private textEndsWithListeners = new Map<
    string,
    Array<{ substring: string; callbackFn: (ev: { textId: string; currentText: string }) => void }>
  >();

  private textChangeListeners = new Map<
    string | "*",
    Array<(ev: { textId: string; newText: string; oldText: string }) => void>
  >();

  // ============ générique ============

  public on(eventName: string, callback: EventCallback) {
    this.getArr(this.genericListeners, eventName).push(callback);
    return () => this.off(eventName, callback);
  }

  public off(eventName: string, callback: EventCallback) {
    const arr = this.genericListeners.get(eventName);
    if (!arr) return;
    const i = arr.indexOf(callback);
    if (i !== -1) arr.splice(i, 1);
    if (arr.length === 0) this.genericListeners.delete(eventName);
  }

  public emit(eventName: string, ...args: any[]) {
    const arr = this.genericListeners.get(eventName);
    if (arr) for (const fn of [...arr]) { try { fn(...args); } catch (e) { console.error(e); } }

    // 👉 Miroir global (navigateur)
    try {
      (globalThis as any)?.dispatchEvent?.(
        new CustomEvent(`tilmi:${eventName}`, { detail: args })
      );
    } catch { /* noop */ }
  }

  // ============ pickup ============

  public addPickupListener(charId: string, objId: string, callbackFn: PickupCallbackFn) {
    const key = this.normalizeCollisionKey(charId, objId);
    this.getArr(this.pickupListeners, key).push(callbackFn);
    return () => this.removePickupListener(charId, objId, callbackFn);
  }

  public removePickupListener(charId: string, objId: string, callbackFn: PickupCallbackFn) {
    const key = this.normalizeCollisionKey(charId, objId);
    this.removeFromMapArray(this.pickupListeners, key, callbackFn);
  }

  public dispatchPickupEvent(charId: string, objId: string) {
    const key = this.normalizeCollisionKey(charId, objId);
    const arr = this.pickupListeners.get(key);
    if (arr) {
      const ev = { charId, objId };
      arr.forEach((fn) => fn(ev));
    }
    this.emit("pickupEvent", charId, objId);
  }

  // ============ 1) collisions perso-perso ============

  public addCollisionListener(entityA: string, entityB: string, callbackFn: CollisionCallbackFn) {
    const key = this.normalizeCollisionKey(entityA, entityB);
    this.getArr(this.characterCollisionListeners, key).push(callbackFn);
    return () => this.removeCollisionListener(entityA, entityB, callbackFn);
  }

  public removeCollisionListener(entityA: string, entityB: string, callbackFn: CollisionCallbackFn) {
    const key = this.normalizeCollisionKey(entityA, entityB);
    this.removeFromMapArray(this.characterCollisionListeners, key, callbackFn);
  }

  public dispatchCollisionEvent(entityA: string, entityB: string) {
    const key = this.normalizeCollisionKey(entityA, entityB);
    const arr = this.characterCollisionListeners.get(key);
    if (arr) {
      const eventData: CollisionEventData = { entityA, entityB };
      arr.forEach((fn) => fn(eventData));
    }
    this.emit("collisionEvent", entityA, entityB);
  }

  private normalizeCollisionKey(a: string, b: string): string {
    return [a, b].sort().join("|"); // ordre indifférent
  }

  // ============ 2) collisions perso-objet ============

  public addCharacterObjectCollisionListener(
    charId: string,
    objId: string,
    callbackFn: CharacterObjectCollisionCallbackFn
  ) {
    const key = this.normalizeCollisionKey(charId, objId);
    this.getArr(this.characterObjectCollisionListeners, key).push(callbackFn);
    return () => this.removeCharacterObjectCollisionListener(charId, objId, callbackFn);
  }

  public removeCharacterObjectCollisionListener(
    charId: string,
    objId: string,
    callbackFn: CharacterObjectCollisionCallbackFn
  ) {
    const key = this.normalizeCollisionKey(charId, objId);
    this.removeFromMapArray(this.characterObjectCollisionListeners, key, callbackFn);
  }

  public dispatchCharacterObjectCollisionEvent(charId: string, objId: string) {
    const key = this.normalizeCollisionKey(charId, objId);
    const arr = this.characterObjectCollisionListeners.get(key);
    if (arr) {
      const eventData = { charId, objId };
      arr.forEach((fn) => fn(eventData));
    }
    this.emit("characterObjectCollisionEvent", charId, objId);
  }

  // ============ 3) reach position ============

  private normalizeReachPosKey(charId: string, x: number, y: number) {
    return `${charId}|${x}|${y}`;
  }

  public addReachPositionListener(charId: string, x: number, y: number, callbackFn: ReachPositionCallbackFn) {
    const key = this.normalizeReachPosKey(charId, x, y);
    this.getArr(this.reachPositionListeners, key).push(callbackFn);
    return () => this.removeReachPositionListener(charId, x, y, callbackFn);
  }

  public removeReachPositionListener(charId: string, x: number, y: number, callbackFn: ReachPositionCallbackFn) {
    const key = this.normalizeReachPosKey(charId, x, y);
    this.removeFromMapArray(this.reachPositionListeners, key, callbackFn);
  }

  public dispatchReachPositionEvent(charId: string, x: number, y: number) {
    const key = this.normalizeReachPosKey(charId, x, y);
    const arr = this.reachPositionListeners.get(key);
    if (arr) {
      const eventData = { charId, x, y };
      arr.forEach((fn) => fn(eventData));
    }
    this.emit("reachPositionEvent", charId, x, y);
  }

  // ============ 4) key press ============

  public addKeyPressListener(keyName: string, callbackFn: KeyPressCallbackFn) {
    this.getArr(this.keyPressListeners, keyName).push(callbackFn);
    return () => this.removeKeyPressListener(keyName, callbackFn);
  }

  public removeKeyPressListener(keyName: string, callbackFn: KeyPressCallbackFn) {
    this.removeFromMapArray(this.keyPressListeners, keyName, callbackFn);
  }

  public dispatchKeyPressEvent(keyName: string) {
    const arr = this.keyPressListeners.get(keyName);
    if (arr) {
      const eventData = { key: keyName };
      arr.forEach((fn) => fn(eventData));
    }
    this.emit("keyPressEvent", keyName);
  }

  // ============ 5) click object ============

  public addClickObjectListener(objId: string, callbackFn: ClickObjectCallbackFn) {
    this.getArr(this.clickObjectListeners, objId).push(callbackFn);
    return () => this.removeClickObjectListener(objId, callbackFn);
  }

  public removeClickObjectListener(objId: string, callbackFn: ClickObjectCallbackFn) {
    this.removeFromMapArray(this.clickObjectListeners, objId, callbackFn);
  }

  public dispatchClickObjectEvent(objId: string) {
    const arr = this.clickObjectListeners.get(objId);
    if (arr) {
      const eventData = { objId };
      arr.forEach((fn) => fn(eventData));
    }
    this.emit("clickObjectEvent", objId);
  }

  // ============ 6) timer event ============

  public addTimerEvent(seconds: number, callbackFn: TimerCallbackFn, repeat = false) {
    if (!repeat) {
      const id = setTimeout(() => {
        const eventData = { seconds };
        try { callbackFn(eventData); } finally { this.emit("timerEvent", seconds); }
        // auto-retire du registre timeouts
        this.timeoutIds = this.timeoutIds.filter((t) => t !== id);
      }, seconds * 1000);
      this.timeoutIds.push(id);
      return () => {
        clearTimeout(id);
        this.timeoutIds = this.timeoutIds.filter((t) => t !== id);
      };
    } else {
      const id = setInterval(() => {
        const eventData = { seconds };
        callbackFn(eventData);
        this.emit("timerEvent", seconds);
      }, seconds * 1000);
      this.intervalIds.push(id);
      return () => {
        clearInterval(id);
        this.intervalIds = this.intervalIds.filter((i) => i !== id);
      };
    }
  }

  // ============ 7) text_starts_with ============

  public addTextStartsWithListener(
    textId: string,
    substring: string,
    callbackFn: (eventData: { textId: string; currentText: string }) => void
  ) {
    this.getArr(this.textStartsWithListeners, textId).push({ substring, callbackFn });
    return () => this.removeTextStartsWithListener(textId, substring, callbackFn);
  }

  public removeTextStartsWithListener(
    textId: string,
    substring: string,
    callbackFn: (eventData: { textId: string; currentText: string }) => void
  ) {
    const arr = this.textStartsWithListeners.get(textId);
    if (!arr) return;
    const idx = arr.findIndex((o) => o.substring === substring && o.callbackFn === callbackFn);
    if (idx !== -1) arr.splice(idx, 1);
    if (arr.length === 0) this.textStartsWithListeners.delete(textId);
  }

  public dispatchTextStartsWithEvent(textId: string, currentText: string) {
    const arr = this.textStartsWithListeners.get(textId);
    if (!arr) return;
    arr.forEach((obj) => {
      if (currentText.startsWith(obj.substring)) {
        obj.callbackFn({ textId, currentText });
      }
    });
  }

  // ============ 8) text_ends_with ============

  public addTextEndsWithListener(
    textId: string,
    substring: string,
    callbackFn: (eventData: { textId: string; currentText: string }) => void
  ) {
    this.getArr(this.textEndsWithListeners, textId).push({ substring, callbackFn });
    return () => this.removeTextEndsWithListener(textId, substring, callbackFn);
  }

  public removeTextEndsWithListener(
    textId: string,
    substring: string,
    callbackFn: (eventData: { textId: string; currentText: string }) => void
  ) {
    const arr = this.textEndsWithListeners.get(textId);
    if (!arr) return;
    const idx = arr.findIndex((o) => o.substring === substring && o.callbackFn === callbackFn);
    if (idx !== -1) arr.splice(idx, 1);
    if (arr.length === 0) this.textEndsWithListeners.delete(textId);
  }

  public dispatchTextEndsWithEvent(textId: string, currentText: string) {
    const arr = this.textEndsWithListeners.get(textId);
    if (!arr) return;
    arr.forEach((obj) => {
      if (currentText.endsWith(obj.substring)) {
        obj.callbackFn({ textId, currentText });
      }
    });
  }

  // ============ 9) text_change ============

  public addTextChangeListener(
    textId: string | "*",
    callbackFn: (ev: { textId: string; newText: string; oldText: string }) => void
  ) {
    this.getArr(this.textChangeListeners, textId).push(callbackFn);
    return () => this.removeTextChangeListener(textId, callbackFn);
  }

  public removeTextChangeListener(
    textId: string | "*",
    callbackFn: (ev: { textId: string; newText: string; oldText: string }) => void
  ) {
    this.removeFromMapArray(this.textChangeListeners, textId, callbackFn);
  }

  /** Dispatch interne : appelé par TextObject.setText() */
  public dispatchTextChangeEvent(textId: string, newText: string, oldText: string) {
    const fire = (key: string | "*") => {
      const arr = this.textChangeListeners.get(key);
      if (arr) arr.forEach((fn) => fn({ textId, newText, oldText }));
    };
    fire(textId);
    fire("*");
    this.emit("textChangeEvent", textId, newText, oldText);
  }

  // ============ clearers ============

  /** Supprime absolument tous les listeners & timers */
  public clearAllCollisionListeners() {
    this.characterCollisionListeners.clear();
    this.characterObjectCollisionListeners.clear();
    this.reachPositionListeners.clear();
    this.keyPressListeners.clear();
    this.clickObjectListeners.clear();
    this.pickupListeners.clear();

    // Arrête tous les intervals & timeouts
    for (const id of this.intervalIds) clearInterval(id);
    for (const id of this.timeoutIds) clearTimeout(id);
    this.intervalIds = [];
    this.timeoutIds = [];
  }

  /** Optionnel : wipe total */
  public clearAll() {
    this.clearAllCollisionListeners();
    this.textStartsWithListeners.clear();
    this.textEndsWithListeners.clear();
    this.textChangeListeners.clear();
    this.genericListeners.clear();
  }

  // ============ helpers ============

  private getArr<T>(map: Map<string, T[]>, key: string): T[] {
    let arr = map.get(key);
    if (!arr) {
      arr = [];
      map.set(key, arr);
    }
    return arr;
  }

  private removeFromMapArray<T>(map: Map<string, T[]>, key: string, item: T) {
    const arr = map.get(key);
    if (!arr) return;
    const idx = arr.indexOf(item);
    if (idx !== -1) arr.splice(idx, 1);
    if (arr.length === 0) map.delete(key);
  }
}
